import React from 'react'
import { cn } from './cn'

interface StepperBaseProps {
  /** Current numeric value (controlled). */
  value: number
  /** Fired with the next value (already clamped to [min, max]). */
  onChange: (next: number) => void
  /** Lower bound; the decrement button disables at it. Default 0. */
  min?: number
  /** Upper bound; the increment button disables at it. Default Infinity. */
  max?: number
  /** Amount added/removed per click. Default 1. */
  step?: number
  /** Accessible label for the decrement (−) button — i18n at the call site. */
  decrementLabel: string
  /** Accessible label for the increment (+) button — i18n at the call site. */
  incrementLabel: string
  /** Render the value with units/formatting; the internal value stays numeric. */
  formatValue?: (value: number) => React.ReactNode
  className?: string
}

/**
 * `editable` 讓中間的值變成可輸入的 <input type="number">。開啟時 `inputLabel` 必填
 * （型別強制）：輸入框沒有可見 label，少了 aria-label 就是讀屏讀不出的欄位。
 * `formatValue` 只作用在唯讀模式，可輸入時輸入框裡就是純數字。
 */
export type StepperProps = StepperBaseProps &
  ({ editable: true; inputLabel: string } | { editable?: false; inputLabel?: never })

/**
 * +/- 數字加減控制，對應設計系統的「席次調整」樣式。
 * 只元件化 .stepper —— .step（流程步驟）與 .step-num（步驟編號徽章）是不同概念、不在此。
 * 值的 clamp 在這裡兜底，呼叫端拿到的 onChange 一定落在 [min, max]：按鈕如此，
 * 手打的值也是（onBlur／Enter 才送出；非整數或空值原封退回目前的值）。
 */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  decrementLabel,
  incrementLabel,
  formatValue,
  editable,
  inputLabel,
  className,
}: StepperProps) {
  const atMin = value <= min
  const atMax = value >= max
  const clamp = (next: number) => Math.min(max, Math.max(min, next))
  // null = 顯示 props 的 value；字串 = 使用者正在打字還沒送出。
  const [draft, setDraft] = React.useState<string | null>(null)

  const commitDraft = () => {
    if (draft === null) return
    const parsed = Number(draft.trim())
    setDraft(null)
    if (draft.trim() === '' || !Number.isInteger(parsed)) return
    const next = clamp(parsed)
    if (next !== value) onChange(next)
  }

  return (
    <div
      className={cn(
        'inline-flex h-[var(--ctl-h)] items-center overflow-hidden rounded-[var(--radius)] border border-border bg-background',
        // 焦點環掛在外框（同 <InputAffix>）：內框 overflow-hidden 會把 input 自己的
        // outline 裁掉。唯讀模式沒有可聚焦的輸入框，維持現況不加。
        editable &&
          'focus-within:border-foreground focus-within:outline-2 focus-within:outline-[var(--fg-soft)]',
        className
      )}
    >
      <button
        type="button"
        aria-label={decrementLabel}
        disabled={atMin}
        className="grid h-full min-w-9 place-items-center border-0 border-r border-border bg-transparent text-lg text-muted hover:bg-surface hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => {
          if (!atMin) onChange(clamp(value - step))
        }}
      >
        −
      </button>
      {editable ? (
        <input
          type="number"
          inputMode="numeric"
          aria-label={inputLabel}
          value={draft ?? String(value)}
          min={min}
          max={Number.isFinite(max) ? max : undefined}
          className="h-full w-12 border-0 bg-transparent px-2 text-center font-mono text-sm tabular-nums [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitDraft()
            } else if (event.key === 'Escape') {
              setDraft(null)
            }
          }}
        />
      ) : (
        <span className="min-w-11 px-2 text-center font-mono text-sm tabular-nums">
          {formatValue ? formatValue(value) : value}
        </span>
      )}
      <button
        type="button"
        aria-label={incrementLabel}
        disabled={atMax}
        className="grid h-full min-w-9 place-items-center border-0 border-l border-border bg-transparent text-lg text-muted hover:bg-surface hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => {
          if (!atMax) onChange(clamp(value + step))
        }}
      >
        +
      </button>
    </div>
  )
}
